import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from '@saleshandy/icons';
import { SubscriptionPlans } from '../../../../../shared/utils/subscription-plans';
import { Images } from '../../../../../shared/app-constants';
import {
  getRestrictionWarning,
  getRestrictionWarningTooltip,
  getTrailPlanWarningString,
} from '../utils/helper';
import { OverlayTooltip } from '../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import { getUpgradePlanCTA } from '../../../../../shared/utils/get-upgrade-cta';
import hasPermission from '../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../shared/utils/access-control/enums/permissions';
import { isPlanDowngradedFromTrial } from '../../../utils/plan-transition';
import store from '../../../../../store';
import { isAgencificationClientOrAgencyUser } from '../../../../../shared/utils/user-details';

type BannerMetaData = {
  show: boolean;
  type?: 'warning' | 'danger';
  imgSrc?: string;
  message?: string;
  showTooltip?: boolean;
  tooltipText?: string;
  isPostLoadMetaRequestPending?: boolean;
  showTooltipOnButton?: boolean;
  icon?: React.ReactNode;
};

const HeaderBanner = ({
  subscriptionPlan,
  subscriptionEndDays,
  pathname,
  leadFinderCredits,
  handleUpgradePlanCTA,
  isPostLoadMetaRequestPending,
}) => {
  const { t } = useTranslation();
  const state = store.getState();
  const planTransitionDetails = state.home.subscription?.planTransitionDetails;

  const inboxAnalyzerPlanCode =
    state?.home?.inboxAnalyzerSubscription?.planCode;
  const remainingInboxAnalyzerEmailSentLimit = parseInt(
    state.home.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
      'INBOX_ANALYZER.TESTS'
    ] ?? '0',
    10,
  );
  const totalTestsCreated =
    state?.home?.getPostLoadMetaResponse?.totalTestsCreated;

  const isInboxAnalyzerStarterPlan = inboxAnalyzerPlanCode?.includes('basic');
  const isInboxAnalyzerProPlan = inboxAnalyzerPlanCode?.includes('pro') && inboxAnalyzerPlanCode?.includes('11-2025');
  const isInboxAnalyzerProPlanUnlimited = inboxAnalyzerPlanCode?.includes('pro') && inboxAnalyzerPlanCode?.includes('05-2025');
  const isInboxAnalyzerScalePlan = inboxAnalyzerPlanCode?.includes('scale');

  const getBannerMetaData = (): BannerMetaData => {
    const path = pathname.split('/')?.[1];
    const inboxPath = 'inbox-radar';
    const isv2LeadsPath = pathname.includes('/v2/leads');

    if (isAgencificationClientOrAgencyUser()) {
      return {
        show: false,
        type: 'warning',
        imgSrc: '',
        message: '',
        showTooltip: false,
        tooltipText: '',
      };
    }

    if (
      !isPostLoadMetaRequestPending &&
      (path === 'leads' || isv2LeadsPath) &&
      leadFinderCredits < 100 &&
      hasPermission(Permissions.ACCOUNT_SUBSCRIPTION_READ)
    ) {
      return {
        show: true,
        type: 'warning',
        message: 'Your lead finder credits are low.',
        showTooltip: false,
        tooltipText:
          'By purchasing credits, you can reveal both emails and phone numbers of leads',
        showTooltipOnButton: true,
        icon: <AlertTriangle />,
      };
    }

    if (path === inboxPath) {
      if (isPostLoadMetaRequestPending) {
        return {
          show: false,
          type: 'warning',
          imgSrc: '',
          message: '',
          showTooltip: false,
          tooltipText: '',
        };
      }
      if (
        !isPostLoadMetaRequestPending &&
        (inboxAnalyzerPlanCode === SubscriptionPlans.INBOX_ANALYZER_FREE ||
          inboxAnalyzerPlanCode === SubscriptionPlans.INBOX_ANALYZER_TRIAL)
      ) {
        let headerMessage = '';

        if (remainingInboxAnalyzerEmailSentLimit === 0) {
          if (totalTestsCreated === 0) {
            headerMessage = 'This feature is available on our paid plans.';
          } else {
            headerMessage =
              "You've used all your free tests. To create a new test,";
          }
        } else {
          headerMessage = `You have ${remainingInboxAnalyzerEmailSentLimit} trial test${
            remainingInboxAnalyzerEmailSentLimit !== 1 ? 's' : ''
          }.`;
        }

        return {
          show: true,
          type: 'warning',
          imgSrc: Images.AlertCircleOrange,
          message: headerMessage,
          showTooltip: false,
          tooltipText: '',
        };
      }

      if (!isPostLoadMetaRequestPending && isInboxAnalyzerStarterPlan) {
        return {
          show: true,
          type: 'warning',
          imgSrc: Images.AlertCircleOrange,
          message:
            remainingInboxAnalyzerEmailSentLimit === 0
              ? 'You have reached your monthly test limit.'
              : `You have ${remainingInboxAnalyzerEmailSentLimit} tests left for this month.`,
          showTooltip: false,
          tooltipText: '',
        };
      }
      if (!isPostLoadMetaRequestPending && isInboxAnalyzerProPlan) {
        return {
          show: true,
          type: 'warning',
          imgSrc: Images.AlertCircleOrange,
          message:
            remainingInboxAnalyzerEmailSentLimit === 0
              ? 'You have reached your monthly test limit.'
              : `You have ${remainingInboxAnalyzerEmailSentLimit} tests left for this month.`,
          showTooltip: false,
          tooltipText: '',
        };
      }
      if (!isPostLoadMetaRequestPending && isInboxAnalyzerScalePlan) {
        return {
          show: true,
          type: 'warning',
          imgSrc: Images.AlertCircleOrange,
          message:
            remainingInboxAnalyzerEmailSentLimit === 0
              ? 'You have reached your monthly test limit.'
              : `You have ${remainingInboxAnalyzerEmailSentLimit} tests left for this month.`,
          showTooltip: false,
          tooltipText: '',
        };
      }
      if (!isPostLoadMetaRequestPending && isInboxAnalyzerProPlanUnlimited) {
        return {
          show: false,
          type: 'warning',
          imgSrc: '',
          message: '',
          showTooltip: false,
          tooltipText: '',
        };
      }

      return {
        show: true,
        type: 'warning',
        imgSrc: Images.AlertCircleOrange,
        message: 'This feature is available on our paid plans.',
        showTooltip: false,
        tooltipText: '',
      };
    }

    if (subscriptionPlan === SubscriptionPlans.TRIAL) {
      return {
        show: true,
        type: 'warning',
        imgSrc: Images.AlertTriangle,
        message: getTrailPlanWarningString({ t, subscriptionEndDays }),
        showTooltip: false,
        tooltipText: '',
      };
    }

    if (subscriptionPlan === SubscriptionPlans.FREE) {
      if (isPlanDowngradedFromTrial(planTransitionDetails)) {
        return {
          show: true,
          type: 'danger',
          imgSrc: Images.AlertTriangleRed,
          message: 'Your trial period is expired.',
          showTooltip: false,
          tooltipText: '',
        };
      }
      return {
        show: true,
        type: 'danger',
        imgSrc: Images.AlertTriangleRed,
        message: 'Your plan has expired.',
        showTooltip: false,
        tooltipText: '',
      };
    }

    const warningMessage = getRestrictionWarning();
    if (warningMessage) {
      return {
        show: true,
        type: 'danger',
        imgSrc: Images.AlertTriangleRed,
        message: warningMessage,
        showTooltip: true,
        tooltipText: getRestrictionWarningTooltip(),
      };
    }

    return {
      show: false,
      type: 'warning',
      imgSrc: '',
      message: '',
      showTooltip: false,
      tooltipText: '',
    };
  };

  const {
    show,
    type,
    imgSrc,
    message,
    showTooltip,
    tooltipText,
    showTooltipOnButton,
    icon,
  } = getBannerMetaData();

  const bannerMsgJSX = (
    <span className="regular-1 font-medium popover-arrow-color-txt ml-1">
      {message}{' '}
    </span>
  );

  const headerCta = getUpgradePlanCTA();

  return (
    show && (
      <div className={`bs-banner mr-2 bs-banner-${type}`}>
        {icon && icon}
        {imgSrc && <img src={imgSrc} alt={type} />}

        {showTooltip ? (
          <OverlayTooltip text={tooltipText}>{bannerMsgJSX}</OverlayTooltip>
        ) : (
          bannerMsgJSX
        )}
        {showTooltipOnButton ? (
          <OverlayTooltip
            text={tooltipText}
            className="lead-finder-add-credits-tooltip"
          >
            <span
              role="button"
              className="bs-banner__cta regular-1 font-medium popover-arrow-color-txt"
              {...accessibleOnClick(() => handleUpgradePlanCTA())}
            >
              {t(`labels.add_credits`)}
            </span>
          </OverlayTooltip>
        ) : headerCta && (
            <span
              role="button"
              className="bs-banner__cta regular-1 font-medium popover-arrow-color-txt"
              {...accessibleOnClick(() => handleUpgradePlanCTA())}
            >
              {t(`labels.${headerCta}`)}
            </span>
        )}
      </div>
    )
  );
};

export default HeaderBanner;
